import React, { useState, useEffect } from 'react';
import { Controller, Control, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { FileUploadInput } from '@/components/shared';
import { useFileUpload } from '@/hooks/form-handlers/useFileUpload';

interface FamilyDocumentsFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
    layout?: 'full' | 'compact';
}

const FamilyDocumentsFieldset: React.FC<FamilyDocumentsFieldsetProps> = ({
    context,
    collaboratorId,
    control,
    setValue,
    watch,
    errors = {},
    isSubmitting = false,
    layout = 'full'
}) => {
    // Estado local para os dados do formulário
    const [formData, setFormData] = useState<CollaboratorFormData>({} as CollaboratorFormData);

    // Hook de upload de arquivos
    const { handleFileChange, uploadStates } = useFileUpload(setFormData);

    // Subscribe para mudanças do formulário
    useEffect(() => {
        const subscription = watch((value) => {
            setFormData(value as CollaboratorFormData);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Helper para verificar se campos são obrigatórios
    const isRequired = (fieldName: string) => collaboratorSchemaHelpers.isFieldRequired(fieldName, context);

    // Dados atuais do formulário
    const watchedData = watch();
    const maritalStatus = watchedData.maritalStatus;
    const children = watchedData.children || [];

    // Verificar se deve mostrar documentos do cônjuge
    const shouldShowSpouseDocuments = () => {
        return (maritalStatus === 'Casado(a)' || maritalStatus === 'União Estável') && 
               (shouldShow('marriageCertificateFile') || shouldShow('spouseIdentityFile'));
    };

    // Verificar se deve mostrar documentos dos pais
    const shouldShowParentDocuments = () => {
        return shouldShow('motherIdentityFile') || shouldShow('fatherIdentityFile');
    };

    // Early return se não deve mostrar nenhum documento familiar
    if (!shouldShowSpouseDocuments() && children.length === 0 && !shouldShowParentDocuments()) {
        return null;
    }

    return (
        <fieldset className="border p-4 rounded-md">
            <legend className="text-sm font-medium px-1 text-gray-400">Documentos Familiares</legend>
            <div className="space-y-6 pt-4">

                {/* Documentos do Cônjuge */}
                {shouldShowSpouseDocuments() && (
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-4">
                            Documentos do Cônjuge
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            
                            {/* Certidão de Casamento */}
                            {shouldShow('marriageCertificateFile') && (
                                <div>
                                    <Controller
                                        name="marriageCertificateFile"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <FileUploadInput
                                                name="marriageCertificateFile"
                                                label="Certidão de Casamento/União Estável (Opcional)"
                                                fileUrl={watchedData.marriageCertificateUrl}
                                                fileName={watchedData.marriageCertificateFile?.name}
                                                isSubmitting={isSubmitting}
                                                isRequired={false}
                                                onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;

                                                    field.onChange(file);
                                                    
                                                    // Upload do arquivo e atualização do URL no React Hook Form
                                                    try {
                                                        const downloadURL = await handleFileChange(e, {
                                                            fieldName: 'marriageCertificateFile',
                                                            urlFieldName: 'marriageCertificateUrl',
                                                            pathPrefix: 'marriage_certificates',
                                                            id: collaboratorId
                                                        });
                                                        
                                                        // Atualizar React Hook Form com a URL
                                                        if (downloadURL) {
                                                            setValue('marriageCertificateUrl', downloadURL);
                                                        }
                                                    } catch (error) {
                                                        console.error('Erro no upload da certidão de casamento:', error);
                                                    }
                                                }}
                                                progress={uploadStates['marriageCertificateFile']?.progress}
                                                status={uploadStates['marriageCertificateFile']?.status || 'idle'}
                                                error={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                </div>
                            )}

                            {/* RG do Cônjuge */}
                            {shouldShow('spouseIdentityFile') && (
                                <div>
                                    <Controller
                                        name="spouseIdentityFile"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <FileUploadInput
                                                name="spouseIdentityFile"
                                                label="RG/Identidade do Cônjuge (Opcional)"
                                                fileUrl={watchedData.spouseIdentityUrl}
                                                fileName={watchedData.spouseIdentityFile?.name}
                                                isSubmitting={isSubmitting}
                                                isRequired={false}
                                                onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;

                                                    field.onChange(file);
                                                    
                                                    // Upload do arquivo e atualização do URL no React Hook Form
                                                    try {
                                                        const downloadURL = await handleFileChange(e, {
                                                            fieldName: 'spouseIdentityFile',
                                                            urlFieldName: 'spouseIdentityUrl',
                                                            pathPrefix: 'family_identity_documents',
                                                            id: collaboratorId
                                                        });
                                                        
                                                        // Atualizar React Hook Form com a URL
                                                        if (downloadURL) {
                                                            setValue('spouseIdentityUrl', downloadURL);
                                                        }
                                                    } catch (error) {
                                                        console.error('Erro no upload da identidade do cônjuge:', error);
                                                    }
                                                }}
                                                progress={uploadStates['spouseIdentityFile']?.progress}
                                                status={uploadStates['spouseIdentityFile']?.status || 'idle'}
                                                error={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Documentos dos Filhos */}
                {children.length > 0 && (
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-4">
                            Documentos dos Filhos
                        </h4>
                        <div className="space-y-6">
                            {children.map((child, index) => {
                                const birthDate = new Date(child.birthDate);
                                const today = new Date();
                                const age = today.getFullYear() - birthDate.getFullYear();
                                const isOver16 = age >= 16;

                                return (
                                    <div key={index} className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                                        <h5 className="text-sm font-medium text-gray-600 mb-3">
                                            {child.name} ({child.sex}, {age} anos)
                                        </h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            
                                            {/* Certidão de Nascimento */}
                                            <div>
                                                <Controller
                                                    name={`children.${index}.birthCertificateFile` as any}
                                                    control={control}
                                                    render={({ field, fieldState }) => (
                                                        <FileUploadInput
                                                            name={`children.${index}.birthCertificateFile`}
                                                            label="Certidão de Nascimento (Opcional)"
                                                            fileUrl={child.birthCertificateUrl}
                                                            fileName={field.value?.name}
                                                            isSubmitting={isSubmitting}
                                                            isRequired={false}
                                                            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const file = e.target.files?.[0];
                                                                if (!file) return;

                                                                field.onChange(file);
                                                                
                                                                // Upload do arquivo e atualização do URL no React Hook Form
                                                                try {
                                                                    const downloadURL = await handleFileChange(e, {
                                                                        fieldName: `children.${index}.birthCertificateFile`,
                                                                        urlFieldName: `children.${index}.birthCertificateUrl`,
                                                                        pathPrefix: 'birth_certificates',
                                                                        id: collaboratorId
                                                                    });
                                                                    
                                                                    // Atualizar React Hook Form com a URL
                                                                    if (downloadURL) {
                                                                        setValue(`children.${index}.birthCertificateUrl` as any, downloadURL);
                                                                    }
                                                                } catch (error) {
                                                                    console.error(`Erro no upload da certidão de nascimento do filho ${index}:`, error);
                                                                }
                                                            }}
                                                            progress={uploadStates[`children.${index}.birthCertificateFile`]?.progress}
                                                            status={uploadStates[`children.${index}.birthCertificateFile`]?.status || 'idle'}
                                                            error={fieldState.error?.message}
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* RG do Filho (somente para maiores de 16 anos) */}
                                            <div>
                                                <Controller
                                                    name={`children.${index}.identityDocumentFile` as any}
                                                    control={control}
                                                    render={({ field, fieldState }) => (
                                                        <FileUploadInput
                                                            name={`children.${index}.identityDocumentFile`}
                                                            label="RG/Identidade (Opcional)"
                                                            fileUrl={child.identityDocumentUrl}
                                                            fileName={field.value?.name}
                                                            isSubmitting={isSubmitting}
                                                            isRequired={false}
                                                            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const file = e.target.files?.[0];
                                                                if (!file) return;

                                                                field.onChange(file);
                                                                
                                                                // Upload do arquivo e atualização do URL no React Hook Form
                                                                try {
                                                                    const downloadURL = await handleFileChange(e, {
                                                                        fieldName: `children.${index}.identityDocumentFile`,
                                                                        urlFieldName: `children.${index}.identityDocumentUrl`,
                                                                        pathPrefix: 'family_identity_documents',
                                                                        id: collaboratorId
                                                                    });
                                                                    
                                                                    // Atualizar React Hook Form com a URL
                                                                    if (downloadURL) {
                                                                        setValue(`children.${index}.identityDocumentUrl` as any, downloadURL);
                                                                    }
                                                                } catch (error) {
                                                                    console.error(`Erro no upload da identidade do filho ${index}:`, error);
                                                                }
                                                            }}
                                                            progress={uploadStates[`children.${index}.identityDocumentFile`]?.progress}
                                                            status={uploadStates[`children.${index}.identityDocumentFile`]?.status || 'idle'}
                                                            error={fieldState.error?.message}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Documentos dos Pais */}
                {shouldShowParentDocuments() && (
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-4">
                            Documentos dos Pais                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            
                            {/* RG da Mãe */}
                            {shouldShow('motherIdentityFile') && (
                                <div>
                                    <Controller
                                        name="motherIdentityFile"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <FileUploadInput
                                                name="motherIdentityFile"
                                                label="RG/Identidade da Mãe (Opcional)"
                                                fileUrl={watchedData.motherIdentityUrl}
                                                fileName={watchedData.motherIdentityFile?.name}
                                                isSubmitting={isSubmitting}
                                                isRequired={false}
                                                onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;

                                                    field.onChange(file);
                                                    
                                                    // Upload do arquivo e atualização do URL no React Hook Form
                                                    try {
                                                        const downloadURL = await handleFileChange(e, {
                                                            fieldName: 'motherIdentityFile',
                                                            urlFieldName: 'motherIdentityUrl',
                                                            pathPrefix: 'parent_documents',
                                                            id: collaboratorId
                                                        });
                                                        
                                                        // Atualizar React Hook Form com a URL
                                                        if (downloadURL) {
                                                            setValue('motherIdentityUrl', downloadURL);
                                                        }
                                                    } catch (error) {
                                                        console.error('Erro no upload da identidade da mãe:', error);
                                                    }
                                                }}
                                                progress={uploadStates['motherIdentityFile']?.progress}
                                                status={uploadStates['motherIdentityFile']?.status || 'idle'}
                                                error={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                </div>
                            )}

                            {/* RG do Pai */}
                            {shouldShow('fatherIdentityFile') && (
                                <div>
                                    <Controller
                                        name="fatherIdentityFile"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <FileUploadInput
                                                name="fatherIdentityFile"
                                                label="RG/Identidade do Pai (Opcional)"
                                                fileUrl={watchedData.fatherIdentityUrl}
                                                fileName={watchedData.fatherIdentityFile?.name}
                                                isSubmitting={isSubmitting}
                                                isRequired={false}
                                                onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;

                                                    field.onChange(file);
                                                    
                                                    // Upload do arquivo e atualização do URL no React Hook Form
                                                    try {
                                                        const downloadURL = await handleFileChange(e, {
                                                            fieldName: 'fatherIdentityFile',
                                                            urlFieldName: 'fatherIdentityUrl',
                                                            pathPrefix: 'parent_documents',
                                                            id: collaboratorId
                                                        });
                                                        
                                                        // Atualizar React Hook Form com a URL
                                                        if (downloadURL) {
                                                            setValue('fatherIdentityUrl', downloadURL);
                                                        }
                                                    } catch (error) {
                                                        console.error('Erro no upload da identidade do pai:', error);
                                                    }
                                                }}
                                                progress={uploadStates['fatherIdentityFile']?.progress}
                                                status={uploadStates['fatherIdentityFile']?.status || 'idle'}
                                                error={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default FamilyDocumentsFieldset;